import { useQuery } from "@apollo/client";
import { FeaturedCard, Maybe, meGQL, Query } from "@onmo/graphql/graphql";
import { getCategoryIcon } from "../constants/services";
import { useFeaturedCards } from "./useFeaturedCards";
import { useMemo } from "react";
import { HomepageMd } from "../models/homepage";
import { useTgpSubscriptions } from "./useTgpSubscriptions";
import { useAppSelector } from "../components/hooks";

interface ServiceConfig {
  title: string;
  link: string;
  usesCustomIcon: boolean;
}

const DEFAULT_SERVICE_CONFIG: Record<string, ServiceConfig> = {
  ca: {
    title: "CA",
    link: "https://challengesarena.co.za/",
    usesCustomIcon: true,
  },
  gamegoza: {
    title: "Game Go",
    link: "https://gamegoza.com/",
    usesCustomIcon: true,
  },
  playinc: {
    title: "PlayInc",
    link: "https://playinc-za-ment.aws.stg.mondia.io/",
    usesCustomIcon: true,
  },
  chessmasters: {
    title: "Chess Masters",
    link: "https://chessmasters.co.za/",
    usesCustomIcon: false,
  },
  unlock3: {
    title: "Unlock 3",
    link: "https://unlock3.co.za/",
    usesCustomIcon: false,
  },
  quizinc: {
    title: "Quizinc 2.0",
    link: "https://quizinc.co.za/",
    usesCustomIcon: false,
  },
  quizhead2head: {
    title: "Head2Head",
    link: "https://quizhead2head.co.za/",
    usesCustomIcon: false,
  },
  stopclock: {
    title: "Stop The Clock",
    link: "https://stopclock.co.za/",
    usesCustomIcon: false,
  },
  blg: {
    title: "Battle League Gaming",
    link: "https://gamezone.blg.gg/",
    usesCustomIcon: false,
  },
} as const;

const SERVICE_CONFIG: Record<string, ServiceConfig> =
  JSON.parse(import.meta.env.VITE_SERVICE_CONFIG) ?? DEFAULT_SERVICE_CONFIG;

export const serviceLinkMap: Record<string, string> = Object.fromEntries(
  Object.entries(SERVICE_CONFIG).map(([key, { link }]) => [key, link]),
);

// Types
interface ServiceItem {
  id: string;
  title: string;
  icon: string;
  link: string;
}

function getServiceIcon(catKey: string, feedItem: FeaturedCard): string {
  const config = SERVICE_CONFIG[catKey];

  if (config?.usesCustomIcon) {
    return getCategoryIcon(catKey)?.src3x ?? "";
  }
  return HomepageMd.getLocalizedAssets(feedItem?.assets)?.icon ?? "";
}

function isFeaturedCard(item: unknown): item is FeaturedCard {
  return (
    item !== null &&
    typeof item === "object" &&
    "metadata" in item &&
    "assets" in item
  );
}

export function useDirectoryData() {
  const { data: dataMe } = useQuery<Query>(meGQL);
  const homeFeed = useFeaturedCards();
  const { theme } = useAppSelector((state) => state.theme);
  const subEnabled = theme?.pages?.directory?.includes("mySubscriptions");
  const { subscriptions: subscriptionData } = useTgpSubscriptions(subEnabled);

  const subscriptions = useMemo((): ServiceItem[] => {
    return (subscriptionData ?? [])
      .map((sub) => {
        const productId = sub.product?.id ?? sub.productId;
        const id = productId === "onmo" ? "gamegoza" : productId;
        const link = id ? serviceLinkMap[id] : undefined;
        if (!id || !link) return null;
        const icon = sub.product?.icon ?? "";

        return { id, title: sub.product?.title ?? "", icon, link };
      })
      .filter((item): item is ServiceItem => item !== null);
  }, [subscriptionData, homeFeed]);

  const tgpLikedItemsMeta: Maybe<string>[] =
    dataMe?.me?.metadata?.tgpLikedItems ?? [];
  const recentlyPlayedMeta: Maybe<string>[] =
    dataMe?.me?.metadata?.tgpRecentlyPlayedItems ?? [];

  const gameById = useMemo(() => {
    const entries = homeFeed
      ?.map((game: FeaturedCard) => (game?.id ? [game.id, game] : null))
      .filter((entry): entry is [string, FeaturedCard] => entry !== null);

    return new Map(entries || []);
  }, [homeFeed]);

  const listService = useMemo((): ServiceItem[] => {
    if (!homeFeed?.length) return [];

    return Object.entries(SERVICE_CONFIG)
      .map(([catKey, { title, link }]) => {
        const feedItem = homeFeed.find(
          (item) =>
            isFeaturedCard(item) && item.metadata.internalProduct === catKey,
        );

        if (!feedItem || !isFeaturedCard(feedItem)) return null;

        const icon = getServiceIcon(catKey, feedItem);
        if (!icon) return null;

        return { id: catKey, title, icon, link };
      })
      .filter((item): item is ServiceItem => item !== null);
  }, [homeFeed]);
  const favouriteGames: FeaturedCard[] = useMemo(() => {
    return tgpLikedItemsMeta
      .map((id) => gameById.get(id ?? ""))
      .filter((game): game is FeaturedCard => Boolean(game));
  }, [tgpLikedItemsMeta, gameById]);

  const recentlyPlayed: FeaturedCard[] = useMemo(() => {
    return recentlyPlayedMeta
      .map((id) => gameById.get(id ?? ""))
      .filter((game): game is FeaturedCard => Boolean(game));
  }, [recentlyPlayedMeta, gameById]);

  const allGames: FeaturedCard[] = useMemo(() => {
    if (!homeFeed?.length) return [];
    return homeFeed.filter((entry): entry is FeaturedCard => entry !== null);
  }, [recentlyPlayedMeta, gameById]);

  return {
    favouriteGames,
    recentlyPlayed,
    listService,
    subscriptions,
    allGames,
  };
}
